import { useEffect, useRef, useReducer } from 'react';

import {
    ColumnChooser,
    GridComponent,
    Inject,
    Selection,
    Sort,
    Toolbar,
    FilterSettingsModel,
    Filter,
} from '@syncfusion/ej2-react-grids';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';

import { requests } from 'common/axios';
import { formatUTCDate } from 'common/widgets';
import {
    ClaimDepartmentGridColumns,
    tabsToolbarOptions,
} from 'components/ProviderData/IpaAdministration/atoms/IpaAdministration.Metadata';
import { AdditionalContact } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { showConfirmation, permissionLevels } from 'components/ProviderData/IpaAdministration/atoms/utils';
import styles from 'components/ProviderData/IpaAdministration/organisms/ClaimDepartment/ClaimDepartment.module.css';
import { ClaimDepartmentForm } from 'components/ProviderData/IpaAdministration/organisms/ClaimDepartment/ClaimDepartmentForm';
import {
    initialClaimDepartmentTabState,
    updateClaimDepartmentTabState,
} from 'components/ProviderData/IpaAdministration/organisms/ClaimDepartment/ClaimDepartmentTab.state';
import { claimDepartmentService } from 'components/ProviderData/subjects/IpaClaimDepartmentService';
import { CLAIM_INFO_CODE } from 'components/ProviderData/utils/Constants';
import { dialogService } from 'subjects/common/DialogService';

type ClaimDepartmentProps = {
    id: number;
    claimDepartmentPermissions: string;
    risk: string;
    ipaCode: string;
    printMemberIdCardFlag: boolean;
};

const ClaimDepartmentTab = (props: ClaimDepartmentProps) => {
    const isReadOnlyViewer = props.claimDepartmentPermissions === permissionLevels.readOnly;
    const [state, dispatch] = useReducer(updateClaimDepartmentTabState, initialClaimDepartmentTabState);
    const toastRef = useRef<ToastComponent>(null);
    const contactGridRef = useRef<GridComponent>(null);

    const filterOptions: FilterSettingsModel = {
        type: 'Excel',
    };

    useEffect(() => {
        updateDataSource();
        claimDepartmentService.get$().subscribe((contact) => dispatch({ type: 'Contact', payload: contact }));
        const subscription = claimDepartmentService.response$().subscribe(async (response) => {
            if (response) {
                if (response.id !== props.id) return;
                if (response.error) {
                    toastRef.current?.show({
                        title: 'Error',
                        content: response.message,
                        cssClass: 'e-toast-danger',
                    });
                } else {
                    toastRef.current?.show({
                        title: 'Success !',
                        content: response.message,
                        cssClass: 'e-toast-success',
                    });
                    updateDataSource();

                    if (response.triggerIdCardWorkflow && props.printMemberIdCardFlag) {
                        const shouldCreateWorkflow = await showConfirmation({
                            title: 'Creating Workflow',
                            message: `
<div>
<div>You have made changes to one or more of the following fields:</div>
<br />
<ul>
<li><b>Claim Address</b></li>
<li><b>Is Claim Address Display on IDCard</b></li>
</ul>
<div>Do you want to create a workflow for <i>PrintMemberIdCard</i>?</div>
</div>
`,
                            confirmBtnText: 'Yes, Create Workflow',
                            cancelBtnText: 'No, Reject',
                            showCloseIcon: false,
                            closeOnEscape: false,
                        });
                        const url = '/api/PrintMemberIdCardWorkflow/CreateWorkflow';
                        dispatch({ type: 'Loading', payload: true });

                        try {
                            await requests.post(url, { ipaCode: props.ipaCode, isRejected: !shouldCreateWorkflow });
                            toastRef.current?.show({
                                title: shouldCreateWorkflow ? 'Success!' : 'Rejection Logged',
                                content: shouldCreateWorkflow ? 'PrintMemberIdCard Workflow created' : 'PrintMemberIdCard Workflow rejection logged',
                                cssClass: shouldCreateWorkflow ? 'e-toast-success' : 'e-toast-info',
                            });
                        } catch (error) {
                            console.error(error);
                            toastRef.current?.show({
                                title: 'Failure!',
                                content: shouldCreateWorkflow ? 'Failed to create PrintMemberIdCard Workflow' : 'Failed to log PrintMemberIdCard Workflow rejection',
                                cssClass: 'e-toast-danger',
                            });
                        } finally {
                            dispatch({ type: 'Loading', payload: false });
                        }
                    }
                }
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, []);

    function handleFormDirtyChange(isDirty) {
        dispatch({ type: 'IsDirty', payload: isDirty });
    }

    function showMessageNoAccess() {
        toastRef.current?.show({
            title: 'Error',
            content: 'You do not have acces to this section.',
            cssClass: 'e-toast-danger',
        });

        return <p>No access to this section</p>;
    }

    const saveContactsButtons = [
        {
            buttonModel: {
                disabled: isReadOnlyViewer,
                content: 'Save',
                iconCss: 'e-icons e-save-icon',
                cssClass: 'e-submit',
            },
            click: () => {
                claimDepartmentService.submit();
            },
        },
        {
            buttonModel: {
                disabled: isReadOnlyViewer,
                content: 'Cancel',
                iconCss: 'e-icons e-cancel-icon',
                cssClass: 'e-cancel',
            },
            click: () => {
                dialogService.close();
                dispatch({ type: 'Contact', payload: null });
            },
        },
    ];

    const updateDataSource = () => {
        const searchContactsUrl = `/api/AdditionalContact/SearchAdditionalContacts?ipaWebId=${props.id}&departmentCodeToInclude=${CLAIM_INFO_CODE}`;

        requests.get<any>(searchContactsUrl).then((data) => {
            const formattedData = data.map((d) => {
                let claimAddress = d.claimAddress;
                let claimAddress2 = '';

                if (d.claimAddress.includes(';')) {
                    const splitClaimAddress = d.claimAddress.split(';');

                    if (splitClaimAddress.length === 2) {
                        claimAddress = `${splitClaimAddress[0]?.trim()}`;
                        claimAddress2 = splitClaimAddress[1]?.trim();
                    } else {
                        claimAddress = splitClaimAddress[0]?.trim();
                        let restOfString = '';

                        for (let position = 0; position < splitClaimAddress.length; position += 1) {
                            if (position !== 0) {
                                restOfString += `${splitClaimAddress[position]} `;
                            }
                        }
                        claimAddress2 = restOfString.trim();
                    }
                }

                return {
                    ...d,
                    claimAddress,
                    claimAddress2,
                    createdDt: formatUTCDate(d.createdDt),
                    updatedDt: formatUTCDate(d.updatedDt),
                };
            });
            dispatch({ type: 'AdditionalContacts', payload: formattedData });
        });
    };

    const toolbarHandler = (args: any) => {
        if (args.item.id === 'refresh') {
            updateDataSource();
        }
        if (args.item.id === 'create') {
            claimDepartmentService?.select({ ipaWeb_Id: props?.id, ipaRisk: props?.risk } as AdditionalContact);
        }
    };

    useEffect(() => {
        if (contactGridRef && isReadOnlyViewer) {
            // Disable toolbar items.
            contactGridRef.current?.toolbarModule.enableItems(['create'], false);
        }
        if (state.contact) {
            const dialog: any = {};
            dialog.header = 'Save Claim';
            dialog.buttons = saveContactsButtons;
            dialog.content = () => (
                <ClaimDepartmentForm
                    contact={state.contact}
                    id={state.contact?.ipaWeb_Id}
                    onFormDirtyChange={handleFormDirtyChange}
                    isReadOnlyViewer={isReadOnlyViewer}
                    risk={state.contact?.ipaRisk}
                />
            );
            dialog.width = 800;
            dialog.height = 550;
            dialogService.open(dialog);
            dispatch({ type: 'Contact', payload: null });
        }
    }, [state.contact, state.isDirty]);

    return (
        <div className='tab-content tabDiv p-3'>
            {props.claimDepartmentPermissions !== permissionLevels.none ? (
                state.loading ? (
                    <div>
                        <div className={styles['spinner-content']}></div>
                        <div className={`${styles['spinner']} spinner-border`}></div>
                    </div>
                ) : (
                    <div className='grid-container'>
                        <GridComponent
                            id='claimDepartmentGrid'
                            height={450}
                            allowPaging={true}
                            allowSorting={true}
                            columns={ClaimDepartmentGridColumns}
                            dataSource={state.additionalContacts}
                            toolbar={tabsToolbarOptions as any}
                            recordClick={(data) => {
                                const rowDataAndRisk = { ...data.rowData, ipaRisk: props.risk };
                                claimDepartmentService?.select(rowDataAndRisk);
                            }}
                            ref={contactGridRef}
                            toolbarClick={toolbarHandler}
                            showColumnChooser={true}
                            filterSettings={filterOptions}
                            allowFiltering={true}
                        >
                            <Inject services={[Selection, Sort, Toolbar, ColumnChooser, Filter]} />
                        </GridComponent>
                    </div>
                )
            ) : (
                showMessageNoAccess()
            )}
            <ToastComponent
                id='toast_target'
                ref={toastRef}
                title='Login Account'
                content=''
                position={{ X: 'Right', Y: 'Top' }}
            />
        </div>
    );
};

export default ClaimDepartmentTab;
